import React, { useState } from "react";
import { Box, Button, Dialog, Typography } from "@mui/material";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import RelianceStagesHoverCard, { hasStageView } from "./RelianceStagesHoverCard";
import { nextStage, stageName, stageParts, STAGE_ORDER } from "../utils/relianceStages";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const AMBER = { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" };

/** "KYC (step 2 of 3)" — the Reliance step the running automation is on, or null. */
export const currentStepText = (policy) => {
  const part = stageParts(policy && policy.relianceStageView)[0];
  if (!part) return null;
  const step = nextStage(part.stages);
  if (!step) return "the last step (payment)";
  return `${stageName(step)} (step ${STAGE_ORDER.indexOf(step) + 1} of ${STAGE_ORDER.length})`;
};

/**
 * Hold pressed while the automation RUNS the policy: say what it is doing and what Hold will do, then ask.
 * Confirm = the hold request; the automation stops at its next safe step, closes the window and holds the policy —
 * Release continues from the step it stopped at (automation lib/holdStop.js).
 */
const StopRunDialog = ({ policy, onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const open = !!policy;
  const step = open ? currentStepText(policy) : null;
  const name = open ? policy.fullName || policy.firstName || policy.policyId || "This policy" : "";

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm(policy);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      {open && (
        <Box sx={{ p: 2.5 }} data-testid="stop-run-dialog">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "11px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: AMBER.color,
                backgroundColor: AMBER.bg,
                border: `1px solid ${AMBER.border}`,
              }}
            >
              <PauseCircleOutlineIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: NAVY, fontFamily: FONT, lineHeight: 1.3 }}>
                Stop the automation?
              </Typography>
              <Typography sx={{ fontSize: "12.5px", color: "#475467", fontFamily: FONT }}>
                {name} is being processed right now
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 1.5, py: 1, mb: 1.5, borderRadius: "10px", backgroundColor: "#EFF8FF", border: "1px solid #B2DDFF" }}>
            <Typography sx={{ fontSize: "12.5px", color: "#175CD3", fontFamily: FONT, fontWeight: 600 }} data-testid="stop-run-current">
              {step ? `Working on: ${step}` : `Status: ${policy.statusLabel || "In progress"}`}
            </Typography>
          </Box>

          {hasStageView(policy) ? (
            <Box sx={{ mb: 1.5, p: 1.25, border: "1px solid #EAECF0", borderRadius: "10px" }}>
              <RelianceStagesHoverCard relianceStageView={policy.relianceStageView} paymentMethod={policy.Paymentmethod} hideFooter width="100%" />
            </Box>
          ) : null}

          <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.25, "& li": { fontSize: "12.5px", color: "#344054", fontFamily: FONT, lineHeight: 1.6 } }}>
            <li>It stops at the next safe step and its window is closed.</li>
            <li>Finished steps stay done. Release continues from this step with a fresh login.</li>
            <li>A payment already being made finishes first. Nothing is paid twice.</li>
          </Box>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={onCancel} disabled={busy} sx={{ textTransform: "none", color: "#475467" }}>
              Keep running
            </Button>
            <Button
              variant="contained"
              onClick={confirm}
              disabled={busy}
              data-testid="stop-run-confirm"
              sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: AMBER.color, boxShadow: "none", "&:hover": { backgroundColor: "#93370D", boxShadow: "none" } }}
            >
              {busy ? "Stopping…" : "Stop and hold"}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default StopRunDialog;
